import { useQuery } from "@tanstack/react-query";
import { Bar, BarChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { AdminApi } from "../../api/services";
import { PageHeader, Spinner } from "../../components/common";
import { peso } from "../../lib/format";

function Stat({ label, value, testId, sub }: { label: string; value: any; testId: string; sub?: string }) {
  return (
    <div className="fp-card">
      <p className="fp-label">{label}</p>
      <p className="mt-1 text-2xl font-bold text-slate-900" data-testid={testId}>{value}</p>
      {sub && <p className="text-xs text-slate-500">{sub}</p>}
    </div>
  );
}

export default function AdminAnalytics() {
  const { data: a, isLoading } = useQuery({ queryKey: ["admin", "analytics"], queryFn: AdminApi.analytics, refetchInterval: 60000 });
  if (isLoading || !a) return <Spinner />;
  return (
    <div className="space-y-5">
      <PageHeader title="Operational analytics" subtitle="All figures from live server data. Simulated units are counted separately." />
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <Stat label="Total bookings" value={a.total_bookings} testId="analytics-total-bookings" sub={`${a.by_type.emergency || 0} emergency · ${a.by_type.scheduled || 0} scheduled`} />
        <Stat label="Active now" value={a.active_bookings} testId="analytics-active-bookings" />
        <Stat label="Avg. time to accept" value={a.avg_accept_minutes != null ? `${a.avg_accept_minutes} min` : "—"} testId="analytics-avg-accept" />
        <Stat label="Revenue (paid)" value={peso(a.revenue_paid)} testId="analytics-revenue" sub="includes test payments" />
        <Stat label="Patients" value={a.patients} testId="analytics-patients" />
        <Stat label="Active memberships" value={a.active_memberships} testId="analytics-memberships" />
        <Stat label="Responders verified" value={`${a.responders_verified}/${a.responders_total}`} testId="analytics-responders" sub={`${a.responders_simulated} simulated`} />
        <Stat label="Available now" value={a.responders_available} testId="analytics-available" />
      </div>
      <div className="fp-card">
        <p className="fp-label mb-3">Bookings · last 7 days (Asia/Manila)</p>
        <div className="h-64" data-testid="analytics-chart">
          <ResponsiveContainer>
            <BarChart data={a.bookings_last_7_days}>
              <XAxis dataKey="date" tickFormatter={(d) => d.slice(5)} fontSize={12} />
              <YAxis allowDecimals={false} fontSize={12} />
              <Tooltip />
              <Bar dataKey="count" fill="#036248" radius={[6, 6, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
      <div className="fp-card">
        <p className="fp-label mb-3">By status</p>
        <div className="flex flex-wrap gap-2">
          {Object.entries(a.by_status).map(([k, v]) => <span key={k} className="rounded-lg bg-slate-100 px-3 py-1 text-sm capitalize">{k.replace(/_/g, " ")}: <strong>{String(v)}</strong></span>)}
        </div>
      </div>
    </div>
  );
}
